import { Alert, Box, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { FaEye, FaRegCreditCard } from 'react-icons/fa';
import { IoFilterOutline } from "react-icons/io5";
import { MdAttachMoney } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { Can } from '../../../Components/Can';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { FinancialFiltersDrawer } from '../../../Components/Drawer/variants/financial-filters/financial-filters-drawer.component';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { ButtonActionMenu } from '../../../Components/Form/ButtonActionsMenu/button-action-menu.component';
import { Loader } from '../../../Components/Loader';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { Table } from '../../../Components/Table/table.component';
import { Tag } from '../../../Components/Tag/tag.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { BillProductOrderOrdersUseCase } from '../../../Usecase/Orders/bill-products-order-usecase';
import { ListOrdersUseCase } from '../../../Usecase/Orders/list-orders-usecase';
import { normalizeFinancialStatusTitle, normalizeSFinancialStatusColors } from '../../../utils/normalizeFinancialStatus';
import { labelsOrderProductionStage, normalizeOrderProductionStage } from '../../../utils/normalizeProductionStages';
import { ServiceOrderStageEnum } from '../../../Services/api/service-orders/service-order.interface';
import { useListFinancial } from './financial-list.hook';
import { OrderFiltersDrawer } from '../../../Components/Drawer/variants/orders-filters/orders-filters-drawer.component';
import { ReprocessOrderPaymentsUseCase } from '../../../Usecase/OrderPayments/reprocess-order-payments-usecase';
import { BillingFlowEnum, OrderProductionStatusEnum, OrderStatusEnum } from '../../../Services/api/orders/orders.interface';
import { normalizeOrderStatusTitle, normalizeOrderStatusColors } from '../../../utils/normalizeOrderStatus';
import { OrderStatusCell } from '../../../Components/OrderStatusCell/order-status-cell.component';
import { billingFlowLabels, billingFlowOptions, getBillingFlowSuggestion, getOrderBillingFlow, getOrderCouponCode } from '../../../utils/billingFlow';
import { UpdateOrdersUseCase } from '../../../Usecase/Orders/update-order-usecase';
interface IFinancialList {
  listOrdersUseCase: ListOrdersUseCase;
  billProductOrderOrdersUseCase: BillProductOrderOrdersUseCase
  reprocessOrderPaymentsUseCase: ReprocessOrderPaymentsUseCase
  updateOrdersUseCase: UpdateOrdersUseCase
}

const billingFlowTagColors: Record<BillingFlowEnum, { backgroundColor: string; textColor: string }> = {
  [BillingFlowEnum.SALE]: { backgroundColor: '#e0e0e0', textColor: '#424242' },
  [BillingFlowEnum.BONUS]: { backgroundColor: '#fff4bf', textColor: '#7a4d00' },
  [BillingFlowEnum.REWORK]: { backgroundColor: '#dbeafe', textColor: '#1d4ed8' },
  [BillingFlowEnum.SUFRAMA]: { backgroundColor: '#dcfce7', textColor: '#166534' },
};

const FinancialList : FC<IFinancialList> = ({ listOrdersUseCase, billProductOrderOrdersUseCase, reprocessOrderPaymentsUseCase, updateOrdersUseCase }) => {
    const theme = useTheme()
    const navigate = useNavigate()

    const { listOrders, list, data, handleShowDetails, showDetails, setShowDetails, loading, setShowFiltersModal, showFiltersModal, kanbanSchema, billProductsOrder, orderSelectedToBill, setOrderSelectedToBill, handleFilterSelect, currentFilters,  handleReprocessPayment,
      orderToReprocess,
      setOrderToReprocess,
      hasReprocessPermission,
      handlePageChangeOrders,
      handleUpdateSelectedOrderBillingFlow,
      totalRegisters
} = useListFinancial({
      listOrdersUseCase,
      billProductOrderOrdersUseCase,
      reprocessOrderPaymentsUseCase,
      updateOrdersUseCase
    })

    const selectedCouponCode = getOrderCouponCode(orderSelectedToBill);
    const selectedFlowSuggestion = getBillingFlowSuggestion(selectedCouponCode);
    const selectedBillingFlow = getOrderBillingFlow(orderSelectedToBill);
    const selectedHasFlowSuggestion = !!selectedFlowSuggestion && selectedBillingFlow !== selectedFlowSuggestion;
    const [reworkObservations, setReworkObservations] = useState('');
    const isSelectedRework = selectedBillingFlow === BillingFlowEnum.REWORK;
    const canBillSelectedOrder = !isSelectedRework || !!reworkObservations.trim();

    console.log("list: ", list)


    useEffect(() => {
      listOrders({ page: 1})
  }, [])

    useEffect(() => {
      setReworkObservations(orderSelectedToBill?.observations || '')
    }, [orderSelectedToBill?.id])

    // Helper function to get tag props based on status
    const getTagProps = (status: string) => {
        const stage = status as ServiceOrderStageEnum;
        const { bgColor, textColor } = normalizeOrderProductionStage(stage);
        const title = labelsOrderProductionStage[stage] || status;
        
        return {
            title,
            boxStyle: { 
                backgroundColor: bgColor, 
                color: textColor, 
                fontSize: '15px', 
                width: '100%', 
                padding: '12px', 
                whiteSpace: 'normal' 
            },
            titleStyle: { 
                color: textColor, 
                fontSize: '15px', 
                width: '100%', 
                textAlign: 'center', 
                whiteSpace: 'normal' 
            }
        };
    };


    const columns = useMemo(() => {
        const createColumns: any[] = [ 
          {
            field: 'nsOrderId',
            headerName: 'Pedido',
       
            width: 200,
            renderCell: (params: any) => <Box sx={{height: '100%', display: 'flex', alignItems: 'center'}}><Typography>{`#${params.value}`}</Typography></Box>
          },
          {
            field: 'store',
            headerName: 'Loja',
            flex:1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => <Box sx={{width: '100%', py: 1}}><Typography sx={{whiteSpace: 'normal', wordWrap: 'break-word', width: '100%'}}>{`${params?.value || 'Não informado'}`}</Typography></Box>
          },
          {
            field: 'createdAt',
            headerName: 'Data',
            width: 200,
          },
          {
            field: 'customer',
            headerName: 'Cliente',
            flex: 1,
            renderCell: (params: any) => <Box sx={{height: '100%', display: 'flex', alignItems: 'center'}}><Typography >{params?.row?.customer?.name || 'Não informado'}</Typography></Box>
          },
          {
            field: 'totalValue',
            headerName: 'Total',
            width: 200,
            renderCell: (params: any) => <Box sx={{height: '100%', display: 'flex', alignItems: 'center'}}><Typography >{`R$ ${params?.row?.totalValue}`}</Typography></Box>
          },
          {
            field: 'products',
            headerName: 'Produtos',
            width: 80,
            align: 'center',
            renderCell: (params: any) => {
              console.log("params: ", params)
              return <Box sx={{height: '100%', display: 'flex', alignItems: 'center'}}><Typography >{`${params?.row?.products?.length}`}</Typography></Box>
            } 
          },
          {
            field: 'status',
            headerName: 'Status',
            headerAlign: 'center',
            flex: 1,
            minWidth: 150,
            renderCell: (params: any) => (
              <OrderStatusCell 
                status={params.row.status}
                productionStatus={params.row.productionStatus}
                errorMessage={params.row.errorMessage}
              />
            )
          },
          {
            field: 'billing_flow',
            headerName: 'Fluxo',
            headerAlign: 'center',
            align: 'center',
            width: 150,
            renderCell: (params: any) => {
              const flow = getOrderBillingFlow(params.row);
              const couponCode = getOrderCouponCode(params.row);
              const flowSuggestion = getBillingFlowSuggestion(couponCode);
              const hasFlowSuggestion = !!flowSuggestion && flow !== flowSuggestion;
              const flowTagColor = hasFlowSuggestion
                ? { backgroundColor: '#fff3cd', textColor: '#7a4d00' }
                : billingFlowTagColors[flow];

              return (
                <Box sx={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '4px'}}>
                  <Tag
                    title={billingFlowLabels[flow]}
                    boxStyle={{
                      backgroundColor: flowTagColor.backgroundColor,
                      color: flowTagColor.textColor,
                      padding: '8px 12px'
                    }}
                    titleStyle={{ color: flowTagColor.textColor, fontSize: '14px' }}
                  />
                  {hasFlowSuggestion && flowSuggestion && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#7a4d00', textAlign: 'center' }}>
                      Cupom: {billingFlowLabels[flowSuggestion]}
                    </Typography>
                  )}
                </Box>
              )
            }
          },
          {
            field: "actions",
            headerName: "Ações",
            headerAlign: "left",
            width: 120,
            renderCell: (params: any) => <ButtonActionMenu options={[
              {
                title: 'Ver detalhes',
                onClick: () => navigate(`/financial-details/${params.row.id}`),
                icon: <FaEye />
              },
              ...(params.row.status === OrderStatusEnum.AWAITING_INVOICING ? [{
                title: 'Faturar todos os produtos',
                onClick: () => setOrderSelectedToBill(params.row),
                icon: <MdAttachMoney />
              }] : []),
              ...(hasReprocessPermission ? [{
                title: 'Reprocessar Pagamento',
                onClick: () => setOrderToReprocess(params.row.id),
                icon: <FaRegCreditCard />
              }] : [])
              
            ]} />
          }
          
        ];
      
        return createColumns;
      }, [list]);
   

    return (
      <Can permissions={['menu:financial:manage']}>  
      <ConfirmDialog title='Reprocessar Pagamento' onCancel={() => setOrderToReprocess('')} description='Você tem certeza?' open={!!orderToReprocess} onConfirm={() => handleReprocessPayment(orderToReprocess || '')}  /> 
        <BaseScreen>
            <Paper >
              {loading && <Loader />}
              <OrderFiltersDrawer 
                handleSelect={handleFilterSelect} 
                open={showFiltersModal} 
                title='Filtros' 
                onToggle={() => setShowFiltersModal(prev => !prev)} 
              />

              <ConfirmDialog
                open={!!orderSelectedToBill}
                title='Faturar todo o pedido'
                description={selectedHasFlowSuggestion ? 'Atenção: encontramos uma sugestão de fluxo diferente antes de faturar.' : isSelectedRework ? 'Faturamento de retrabalho exige observações.' : 'Você tem certeza?'}
                onCancel={() => setOrderSelectedToBill(undefined)}
                onConfirm={() => billProductsOrder(reworkObservations)}
                confirmButtonTitle='Faturar pedido'
                confirmDisabled={!canBillSelectedOrder}
              >
                {selectedHasFlowSuggestion && selectedFlowSuggestion && (
                  <Alert severity='warning' sx={{ marginTop: '16px' }}>
                    <Stack spacing={1}>
                      <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                        Cupom {selectedCouponCode} indica {billingFlowLabels[selectedFlowSuggestion]}
                      </Typography>
                      <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                        Fluxo atual: {billingFlowLabels[selectedBillingFlow]}. Altere o fluxo antes de faturar para evitar emissão no tipo fiscal errado.
                      </Typography>
                      <FormControl fullWidth sx={{ marginTop: '8px', backgroundColor: '#fff' }}>
                        <InputLabel id="selected-order-billing-flow-label">Fluxo de faturamento</InputLabel>
                        <Select
                          labelId="selected-order-billing-flow-label"
                          label="Fluxo de faturamento"
                          value={selectedBillingFlow}
                          onChange={(event) => handleUpdateSelectedOrderBillingFlow(event.target.value as BillingFlowEnum)}
                        >
                          {billingFlowOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {isSelectedRework && (
                        <TextField
                          required
                          multiline
                          minRows={3}
                          label="Motivo do retrabalho"
                          placeholder="Descreva o motivo do retrabalho antes de faturar"
                          value={reworkObservations}
                          onChange={(event) => setReworkObservations(event.target.value)}
                          error={!reworkObservations.trim()}
                          helperText={!reworkObservations.trim() ? 'Obrigatório para faturamento Retrabalho' : 'Será salvo no pedido antes do faturamento'}
                          sx={{ backgroundColor: '#fff' }}
                        />
                      )}
                    </Stack>
                  </Alert>
                )}
                {!selectedHasFlowSuggestion && isSelectedRework && (
                  <TextField
                    required
                    fullWidth
                    multiline
                    minRows={3}
                    label="Motivo do retrabalho"
                    placeholder="Descreva o motivo do retrabalho antes de faturar"
                    value={reworkObservations}
                    onChange={(event) => setReworkObservations(event.target.value)}
                    error={!reworkObservations.trim()}
                    helperText={!reworkObservations.trim() ? 'Obrigatório para faturamento Retrabalho' : 'Será salvo no pedido antes do faturamento'}
                    sx={{ marginTop: '16px' }}
                  />
                )}
              </ConfirmDialog>
 
              {/* {showDetails &&  kanbanSchema?.columnOrder?.length && <SaleDetailsModal billProductsOrder={billProductsOrder} onClose={() => setShowDetails(undefined)} data={showDetails} fromFinancial type='financial' kanbanSchema={kanbanSchema} canDragProductionKanban={false} canClickProductionKanban={false} canDragFinancialKanban={false} canClickFinancialKanban={true} />} */}

                <BaseContentPage 
                    title='Financeiro'
                    breadcrumb={[{ title: 'Financeiro', link: "/financial-list"}, { title: 'Listagem'}]}
                    search
                    searchFunction={(data) => listOrders({ ...currentFilters, searchBy: data.searchBy, page: data.page })}
                    Action={() => <Button LeftIcon={() => <IoFilterOutline size={18} />} sx={{ width: '100px'}} boxLeftSx={{ gap: '10px', marginLeft: '5px'}} textSx={{ textTransform: 'Capitalize', fontWeight: 500}} onClick={() => setShowFiltersModal(true)}>Filtros</Button>}
                >
                  {!!list?.length ?  <Table data={list} columns={columns} totalCount={totalRegisters} onPageChange={handlePageChangeOrders} /> : <Placeholder description='Nenhum dado encontrado.' /> }

                  
                  
                </BaseContentPage>
              
            </Paper>
                
        </BaseScreen>
        </Can>
    )
}

export { FinancialList };
